import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useViewportWidth from "../../hooks/useViewportWidth";
import imageIcon from "../../assets/image.png";
import { useAuth } from "../../contexts/AuthContext";
import { businessApi, adminApi, stripeApi } from "../../services/api";
import { toast } from "react-toastify";
import { scrollToTop } from "../../components/ScrollToTop";

/**
 * The confirmation dialogs' layer. Above the admin shell's own stack, whose top is the app bar
 * at 1001 — below 900px that bar is raised over the drawer (1000) and its scrim (999) so the
 * hamburger stays tappable while the menu is open (AC-MOBILE-F3). At the modal's old 1000 the
 * bar therefore painted through its scrim, leaving the hamburger live in front of a dialog
 * waiting for an answer and the drawer able to slide out over it. AC-MOBILE-F24.
 */
const MODAL_Z_INDEX = 1100;

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "20px",
  padding: 0,
  border: "none",
  background: "transparent",
  color: "#DE810E",
  fontSize: "15px",
  // line-height: normal resolves off the font's own metrics and lands on a fraction of a pixel
  // here, which every box below this one then inherits as a fractional offset — enough to
  // leave the Save button 0.5% below the fold on a phone once the page is scrolled to the end.
  // A whole number keeps the column on whole pixels. AC-MOBILE-F10.
  lineHeight: 1,
  fontWeight: "bold",
  fontFamily: "inherit",
  cursor: "pointer",
};

/**
 * The Stripe vendor id for a partner looking at their own business.
 *
 * `GET /api/business?business_id=` strips it on purpose: that route is the one un-siloed
 * business read (the mobile app's brewery details screen), and issue #712 was a consumer
 * naming a business_id to obtain a brewery's Connect account id. So the Payment card below
 * fell to "No payout account connected." for a partner whose account is connected, while a
 * super admin saw it — their list read, `/api/business/all`, does carry the field.
 *
 * `GET /api/business/me` is the sanctioned owner view and is left whole. Reading from it is
 * the intended shape of this fix; putting the field back on the by-id response would reopen
 * #712. Preferring the record already in hand keeps this to one extra request, and only in
 * the case that was broken. AC-PAYOUT-F1.
 */
const resolveVendorId = async (profile) => {
  const onRecord = profile?.stripeVendorId || profile?.stripe_vendor_id || "";
  if (onRecord) return onRecord;
  try {
    const me = await businessApi.getMyProfile();
    return me?.stripeVendorId || me?.stripe_vendor_id || "";
  } catch (_) {
    // Non-fatal: the rest of the profile has already loaded, and the Payment card keeps the
    // empty state it would have shown anyway.
    return "";
  }
};

const BusinessProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the list was when this profile was opened — the page and query the row was on, handed
  // over by Dashboard.js. Absent on a deep link or a redirect, and then the back control does
  // what it always did and goes to the top of the list. AC-BIZSEARCH-F6.
  const backToList = location.state?.listUrl || "/admin-dashboard";
  const { tokens, isBusinessAdmin, isSuperAdmin } = useAuth();
  // The header below is a row of two on a desktop and a block of three on a phone — a
  // different arrangement, not different measurements, which is what needs the width rather
  // than a clamp. AC-MOBILE-F18.
  const isMobile = useViewportWidth() < 900;
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableFields, setEditableFields] = useState({
    name: "",
    ownerName: "",
    phone: "",
    website: "",
    address: "",
    description: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    website: "",
  });
  const [isStatusButtonHovered, setIsStatusButtonHovered] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutError, setPayoutError] = useState("");
  const fileInputRef = useRef(null);

  // Both dialogs below are `position: fixed`, so the document behind them is still the
  // scroller: a swipe on the dimmed backdrop slid the profile under an unanswered "deactivate
  // this business profile?", and answering it handed back a page somewhere other than where it
  // was left. The same lock the drawer takes (AC-MOBILE-F23), one layer up — and at every
  // width, because a dialog is fixed on a desktop too. AC-MOBILE-F25.
  const statusModalOpen = isSuperAdmin && showStatusModal;
  useEffect(() => {
    if (!(statusModalOpen || showDeleteModal)) {
      return undefined;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [statusModalOpen, showDeleteModal]);

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";
    // Allow phone numbers with +, digits, spaces, dashes, parentheses
    // Examples: +1 234 567 8900, (123) 456-7890, 123-456-7890, +1234567890
    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.trim())) {
      return "Please enter a valid phone number";
    }
    return "";
  };

  const validateWebsite = (website) => {
    if (!website) return "Website is required";
    // Require http:// or https:// protocol
    // Examples: https://example.com, http://example.com
    const websiteRegex =
      /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!websiteRegex.test(website.trim())) {
      return "Please enter a valid website URL";
    }
    return "";
  };

  useEffect(() => {
    const fetchBusinessDetail = async () => {
      if (!tokens.access_token || !id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Decode the business ID from URL
        let businessId = decodeURIComponent(id);
        // Remove "BUSINESS#" prefix if present

        if (isBusinessAdmin) {
          if (businessId.startsWith("BUSINESS#")) {
            businessId = businessId.replace("BUSINESS#", "");
          }
          // For Business Admin, use their own business ID
          const response = await businessApi.getProfile(
            businessId,
            tokens.access_token,
          );

          const businessData = {
            name: response.name || "",
            email: response.email || "",
            phone: response.phone || "",
            address: response.address || "",
            status: response.is_active ? "Active" : "Deactivated",
            PK: response.PK || "",
            ownerName: response.owner_name || "",
            website: response.website || "",
            logoURL: response.logoURL || "",
            description: response.description || "",
            registrationCount: response.registrationCount,
            purchasedCount: response.purchasedCount,
            notRedeemedCount: response.notRedeemedCount,
            stripeVendorId: await resolveVendorId(response),
          };
          setBusiness(businessData);
          // Initialize editable fields
          setEditableFields({
            name: businessData.name,
            ownerName: businessData.ownerName,
            phone: businessData.phone,
            website: businessData.website,
            address: businessData.address,
            description: businessData.description,
          });
          if (businessData.logoURL) {
            setLogoPreview(businessData.logoURL);
          }
        } else if (isSuperAdmin) {
          const profilesResponse =
            await adminApi.getBusinessListWithStripeStatus(tokens.access_token);
          const profileList = Array.isArray(profilesResponse)
            ? profilesResponse
            : Array.isArray(profilesResponse?.businesses)
              ? profilesResponse.businesses
              : [];

          const normalizedId = businessId.startsWith("BUSINESS#")
            ? businessId
            : `BUSINESS#${businessId}`;
          const profile = profileList.find(
            (p) =>
              p.PK === businessId ||
              p.PK === normalizedId ||
              p.business_id === businessId.replace(/^BUSINESS#/, "") ||
              p.businessId === businessId.replace(/^BUSINESS#/, ""),
          );

          if (profile) {
            const businessData = {
              name: profile.name || "",
              email: profile.email || "",
              phone: profile.phone || "",
              address: profile.address || "",
              status: profile.is_active ? "Active" : "Deactivated",
              PK: profile.PK || "",
              ownerName: profile.owner_name || profile.ownerName || "",
              website: profile.website || "",
              logoURL: profile.logoURL || "",
              description: profile.description || "",
              registrationCount:
                profile.registration_count ?? profile.registrationCount,
              purchasedCount: profile.purchased_count ?? profile.purchasedCount,
              notRedeemedCount:
                profile.not_redeemed_count ?? profile.notRedeemedCount,
              stripeVendorId:
                profile.stripeVendorId || profile.stripe_vendor_id || "",
              payoutsEligible: profile.payoutsEligible ?? false,
            };
            setBusiness(businessData);
            // Initialize editable fields
            setEditableFields({
              name: businessData.name,
              ownerName: businessData.ownerName,
              phone: businessData.phone,
              website: businessData.website,
              address: businessData.address,
              description: businessData.description,
            });
            if (businessData.logoURL) {
              setLogoPreview(businessData.logoURL);
            }
          } else {
            toast.error("Business profile not found", {
              position: "top-right",
              autoClose: 4000,
            });
            navigate("/admin-dashboard");
          }
        }
      } catch (error) {
        console.error("Error fetching business detail:", error);
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch business details",
          {
            position: "top-right",
            autoClose: 4000,
          },
        );
        navigate("/admin-dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessDetail();
  }, [id, tokens.access_token, isBusinessAdmin, isSuperAdmin, navigate]);

  useEffect(() => {
    let cancelled = false;

    const fetchPayoutMethod = async () => {
      if (!business?.stripeVendorId || !tokens.access_token) {
        setPayoutMethod(null);
        setPayoutError("");
        return;
      }

      try {
        setPayoutLoading(true);
        setPayoutError("");
        const data = await stripeApi.getDefaultPayoutMethod(
          { stripeVendorId: business.stripeVendorId },
          tokens.access_token,
        );
        if (cancelled) return;
        setPayoutMethod(data || null);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching payout method:", error);
        setPayoutMethod(null);
        setPayoutError(
          error?.response?.data?.error ||
            error?.message ||
            "Failed to load payment information.",
        );
      } finally {
        if (!cancelled) setPayoutLoading(false);
      }
    };

    fetchPayoutMethod();

    return () => {
      cancelled = true;
    };
  }, [business?.stripeVendorId, tokens.access_token]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Result = reader.result;
        setLogoPreview(base64Result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Both mode switches go back to the top, the same way Save does. Edit swaps the read-only panel
  // for a form that starts at its first field and Cancel swaps it back; pressed from halfway down,
  // either one drops the reader into the middle of a layout they have not seen the top of. The
  // three controls sit next to each other, so one of them staying put reads as a fault rather than
  // a choice. AC-SCROLL-F3.
  const handleEdit = () => {
    setIsEditMode(true);
    scrollToTop();
  };

  const handleCancel = () => {
    setIsEditMode(false);
    scrollToTop();
    // Reset editable fields to original business data
    if (business) {
      setEditableFields({
        name: business.name,
        ownerName: business.ownerName,
        phone: business.phone,
        website: business.website,
        address: business.address,
        description: business.description,
      });
      if (business.logoURL) {
        setLogoPreview(business.logoURL);
      } else {
        setLogoPreview(null);
      }
    }
    // Clear validation errors
    setValidationErrors({
      phone: "",
      website: "",
    });
  };

  // Wrapped rather than scrolled at each exit: a save answers in several places — a rejected
  // field, the error toast, the read-only view that a successful one collapses back into — and
  // every one of them is at the top of a form whose Save button is at the bottom. AC-SCROLL-F2.
  const handleSave = async () => {
    try {
      await saveBusinessProfile();
    } finally {
      scrollToTop();
    }
  };

  const saveBusinessProfile = async () => {
    if (!business) return;

    // Validate all fields
    const phoneError = validatePhone(editableFields.phone);
    const websiteError = validateWebsite(editableFields.website);

    setValidationErrors({
      phone: phoneError,
      website: websiteError,
    });

    // Basic validation
    let message = "";
    if (!editableFields.name) message = "Business name is required";
    else if (!editableFields.ownerName) message = "Owner name is required";
    else if (!editableFields.phone) message = "Phone number is required";
    else if (phoneError) message = phoneError;
    else if (!editableFields.website) message = "Website is required";
    else if (websiteError) message = websiteError;
    else if (!editableFields.address) message = "Address is required";
    else if (!editableFields.description) message = "Description is required";

    if (message) {
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Extract business_id from PK
    let businessId = business.PK;
    if (businessId.startsWith("BUSINESS#")) {
      businessId = businessId.replace("BUSINESS#", "");
    }

    if (!businessId || !tokens.access_token) {
      toast.error("Missing business ID or authentication token.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setSaving(true);

      // Build request body
      const requestBody = {
        business_id: businessId,
        name: editableFields.name,
        owner_name: editableFields.ownerName,
        phone: editableFields.phone,
        website: editableFields.website,
        address: editableFields.address,
        description: editableFields.description,
        // Inert, and kept only so the payload mirrors the record. The server treats
        // stripeVendorId as client-immutable and drops it from every public update
        // (business_profile.py CLIENT_IMMUTABLE_FIELDS), so this can neither set nor clear
        // the payout account — that filter is the write half of the #712 fix, which closed
        // an owner repointing their business at another brewery's Connect account. The
        // comment that stood here previously said an empty string would wipe the
        // connection; that stopped being true with #712.
        stripeVendorId: business.stripeVendorId || "",
      };

      // Only include logoURL if a new logo was uploaded (base64 data URL)
      if (logoPreview && logoPreview.startsWith("data:")) {
        requestBody.logoURL = logoPreview;
      }

      const response = await businessApi.updateProfile(
        requestBody,
        tokens.access_token,
      );

      // Update business state with response
      if (response && (response.PK || response.name)) {
        const updatedBusiness = {
          ...business,
          name: response.name || editableFields.name,
          ownerName: response.owner_name || editableFields.ownerName,
          phone: response.phone || editableFields.phone,
          website: response.website || editableFields.website,
          address: response.address || editableFields.address,
          description: response.description || editableFields.description,
        };

        // Update logoURL with the response (in case it was uploaded to S3)
        if (response.logoURL) {
          updatedBusiness.logoURL = response.logoURL;
          setLogoPreview(response.logoURL);
        }

        setBusiness(updatedBusiness);
        setIsEditMode(false);

        toast.success("Profile updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Failed to update profile. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Error updating business profile:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update profile. Please try again.",
        {
          position: "top-right",
          autoClose: 4000,
        },
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleDeleteConfirm = async () => {
    if (!business) return;

    // Extract business_id from PK
    let businessId = business.PK;
    if (businessId.startsWith("BUSINESS#")) {
      businessId = businessId.replace("BUSINESS#", "");
    }

    if (!businessId || !tokens.access_token) {
      toast.error("Missing business ID or authentication token.", {
        position: "top-right",
        autoClose: 3000,
      });
      setShowDeleteModal(false);
      return;
    }

    try {
      setSaving(true);

      await businessApi.deleteProfile(businessId, tokens.access_token);

      toast.success("Business profile deleted successfully!", {
        position: "top-right",
        autoClose: 3000,
      });

      // Navigate back to dashboard after successful deletion
      navigate("/admin-dashboard");
    } catch (error) {
      console.error("Error deleting business profile:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete business profile. Please try again.",
        {
          position: "top-right",
          autoClose: 4000,
        },
      );
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  const handleStatusClick = () => {
    if (!isSuperAdmin) return;
    setShowStatusModal(true);
  };

  const handleStatusCancel = () => {
    if (statusUpdating) return;
    setShowStatusModal(false);
  };

  const handleStatusConfirm = async () => {
    if (!business || !tokens.access_token) return;

    // Toggle current status
    const currentlyActive = business.status === "Active";
    const newIsActive = !currentlyActive;

    // Extract business_id from PK
    let businessId = business.PK;
    if (businessId.startsWith("BUSINESS#")) {
      businessId = businessId.replace("BUSINESS#", "");
    }

    if (!businessId) {
      toast.error("Missing business ID.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setStatusUpdating(true);

      await businessApi.setActiveStatus(
        businessId,
        newIsActive,
        tokens.access_token,
      );

      setBusiness({
        ...business,
        status: newIsActive ? "Active" : "Deactivated",
      });

      toast.success(
        `Business has been ${newIsActive ? "activated" : "deactivated"} successfully.`,
        {
          position: "top-right",
          autoClose: 3000,
        },
      );

      setShowStatusModal(false);
    } catch (error) {
      console.error("Error updating business status:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update business status. Please try again.",
        {
          position: "top-right",
          autoClose: 4000,
        },
      );
    } finally {
      setStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          fontSize: "18px",
          color: "#777",
        }}
      >
        Loading business details...
      </div>
    );
  }

  if (!business) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <h2
          style={{
            textAlign: "left",
            marginBottom: "30px",
            fontSize: "clamp(24px, 6vw, 40px)",
            fontWeight: "bold",
            color: "#303030",
          }}
        >
          Business Profile
        </h2>
        <p style={{ fontSize: "18px", color: "#777" }}>
          Business profile not found.
        </p>
        <button
          onClick={() => navigate(backToList)}
          style={{
            marginTop: "20px",
            padding: "12px clamp(8px, 3vw, 30px)",
            borderRadius: "30px",
            border: "1px solid #DE810E",
            background: "white",
            color: "#DE810E",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: "clamp(14px, 4vw, 18px)",

            whiteSpace: "nowrap",
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Status Change Modal (Super Admin only) */}
      {isSuperAdmin && showStatusModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: MODAL_Z_INDEX,
          }}
          onClick={handleStatusCancel}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "rgba(222, 129, 14, 0.1)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: "30px",
                  color: "#DE810E",
                }}
              >
                !
              </div>

              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#303030",
                  textAlign: "center",
                }}
              >
                Change business status
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "16px",
                  color: "#555",
                  textAlign: "center",
                }}
              >
                Are you sure you want to{" "}
                <span style={{ fontWeight: "bold" }}>
                  {business.status === "Active" ? "deactivate" : "activate"}
                </span>{" "}
                this business profile?
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  width: "100%",
                  marginTop: "10px",
                }}
              >
                <button
                  onClick={handleStatusCancel}
                  disabled={statusUpdating}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    borderRadius: "30px",
                    border: "1px solid #DE810E",
                    background: "white",
                    color: "#DE810E",
                    fontWeight: "bold",
                    cursor: statusUpdating ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleStatusConfirm}
                  disabled={statusUpdating}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    borderRadius: "30px",
                    border: "none",
                    background:
                      business.status === "Active"
                        ? statusUpdating
                          ? "#ccc"
                          : "#CF0A0A"
                        : statusUpdating
                          ? "#ccc"
                          : "#35B44C",
                    color: "white",
                    fontWeight: "bold",
                    cursor: statusUpdating ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  {statusUpdating
                    ? "Updating..."
                    : business.status === "Active"
                      ? "Deactivate"
                      : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: MODAL_Z_INDEX,
          }}
          onClick={handleDeleteCancel}
        >
          <div
            style={{
              background: "white",
              borderRadius: "12px",
              padding: "30px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
              }}
            >
              {/* Error Icon */}
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  background: "rgba(207, 10, 10, 0.1)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: "30px",
                    color: "#CF0A0A",
                  }}
                >
                  ⚠
                </span>
              </div>

              {/* Message */}
              <h3
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#303030",
                  textAlign: "center",
                }}
              >
                Are you sure delete this business profile?
              </h3>

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  width: "100%",
                  marginTop: "10px",
                }}
              >
                <button
                  onClick={handleDeleteCancel}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    borderRadius: "30px",
                    border: "1px solid #DE810E",
                    background: "white",
                    color: "#DE810E",
                    fontWeight: "bold",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "12px 24px",
                    borderRadius: "30px",
                    border: "none",
                    background: saving ? "#ccc" : "#CF0A0A",
                    color: "white",
                    fontWeight: "bold",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: "16px",
                  }}
                >
                  {saving ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The only way back to the list was the browser's own Back button, and this route is
          deep-linkable — arrive at it from a link or a reload and there is no history entry to
          go back to. AC-BIZSEARCH-F5. */}
      <button
        type="button"
        onClick={() => navigate(backToList)}
        data-testid="business-detail-back"
        style={backLinkStyle}
      >
        <span aria-hidden="true" style={{ fontSize: "18px", lineHeight: 1 }}>
          &#8592;
        </span>
        Back to Dashboard
      </button>

      <div
        data-testid="business-header"
        style={{
          display: "flex",
          // A phone gets a block, not a squeezed row. Unwrapped, this row gave the status
          // control whatever the name did not use: "Activate Business" stacked onto two lines
          // inside a pill sized for one, and the pill stood taller than the name beside it.
          // Wrapping alone is not enough either — it puts the badge beside a short name and
          // under a long one, so the same screen has two layouts depending on whose profile is
          // open. A column gives every profile the same three rows in the same order: name,
          // status, then the control across the full width. AC-MOBILE-F18.
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? "14px" : 0,
          marginBottom: "40px",
        }}
      >
        <div>
          {isEditMode ? (
            <input
              type="text"
              value={editableFields.name}
              onChange={(e) =>
                setEditableFields({ ...editableFields, name: e.target.value })
              }
              style={{
                fontSize: "clamp(20px, 5.5vw, 36px)",
                fontWeight: "bold",
                textAlign: "center",
                marginTop: "20px",
                marginBottom: 0,
                border: "1px solid #ddd",
                borderRadius: "5px",
                padding: "8px",
                width: "100%",
                // maxWidth: "300px",
              }}
            />
          ) : (
            <p
              style={{
                // The badge is INLINE in the name, not a flex sibling of it. As a sibling it
                // was laid out after the name's whole box, and a name long enough to wrap
                // fills the row — so the pill ended up pinned to the far right of the header,
                // floating away from the words it belongs to. Inline, it follows the last
                // word wherever that lands, and a short name still reads as "name badge" on
                // one line. This also retires the nowrap/flexShrink pair that was holding the
                // pill on the first line: there is no row for it to be pushed out of now.
                // AC-MOBILE-F18.
                fontSize: "clamp(20px, 5.5vw, 36px)",
                fontWeight: "bold",
                textAlign: "left",
                marginTop: "20px",
                marginBottom: 0,
                overflowWrap: "anywhere",
              }}
            >
              <span>{business.name}</span>
              <span
                style={{
                  // Sized from its label at EVERY width. Above 900px this was a hand-measured
                  // 90px against an 89px "Deactivated" — 1px of slack, so the word sat hard
                  // against both ends of its pill. The phone already sized from the label;
                  // desktop was left on the fixed widths only because that is what the screen
                  // shipped with. AC-MOBILE-F18.
                  ...(isMobile
                    ? { padding: "0 14px", height: "24px", fontSize: "13px" }
                    : { padding: "0 16px", height: "26px", fontSize: "14px" }),
                  // vertical-align middle puts the pill's centre on the name's own optical
                  // centre, which is what centring on a flex line box only approximated.
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  verticalAlign: "middle",
                  marginLeft: "10px",
                  whiteSpace: "nowrap",
                  background:
                    business.status === "Active" ? "#34A853" : "#B40303",
                  borderRadius: "30px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                {business.status}
              </span>
            </p>
          )}
        </div>
        {/* Status inside container */}
        <div
          onClick={isSuperAdmin ? handleStatusClick : undefined}
          onMouseEnter={() => setIsStatusButtonHovered(true)}
          onMouseLeave={() => setIsStatusButtonHovered(false)}
          data-testid="business-status-toggle"
          style={{
            // Taller on a phone, where this control is a full-width row rather than something
            // tucked at the end of a line. The width itself comes from the header's
            // alignItems: stretch — no width declaration here, which is why the criterion is
            // asserted against the rendered box and not against this file. AC-MOBILE-F18.
            padding: isMobile ? "13px 24px" : "10px 24px",
            // The label is one line; the header gives it a row of its own. AC-MOBILE-F18.
            whiteSpace: "nowrap",
            flexShrink: 0,
            background:
              isStatusButtonHovered && isSuperAdmin
                ? business.status === "Active"
                  ? "rgba(180, 3, 3, 0.08)"
                  : "rgba(52, 168, 83, 0.08)"
                : "#ffffff",
            border: `2px solid ${business.status === "Active" ? "#B40303" : "#34A853"}`,
            borderRadius: "9999px",
            display: isSuperAdmin ? "flex" : "none",
            alignItems: "center",
            justifyContent: "center",
            cursor: isSuperAdmin ? "pointer" : "default",
            transition: "background 0.2s ease",
          }}
        >
          <span
            style={{
              color: business.status === "Active" ? "#B40303" : "#34A853",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {business.status === "Active"
              ? "Deactivate Business"
              : "Activate Business"}
          </span>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          // The 200px logo column and the info panel beside it do not both fit a phone;
          // unwrapped, the panel keeps shrinking and its labels get clipped. AC-MOBILE-F9.
          flexWrap: "wrap",
          gap: "clamp(16px, 4vw, 30px)",
          alignItems: "flex-start",
        }}
      >
        {/* ✅ Left - Logo + Business Name */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div
            data-testid="business-logo"
            style={{
              width: "200px",
              height: "200px",
              background: "rgba(222, 129, 14, 0.1)",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "20px",
              overflow: "hidden",
              position: "relative",
              cursor: isEditMode ? "pointer" : "default",
            }}
            onClick={() => {
              if (isEditMode && fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="logo"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <img
                src={imageIcon}
                alt="logo"
                style={{ width: "60px", height: "60px", opacity: 0.7 }}
              />
            )}
            {isEditMode && (
              <div
                style={{
                  position: "absolute",
                  bottom: "10px",
                  right: "10px",
                  background: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  padding: "5px 10px",
                  borderRadius: "5px",
                  fontSize: "12px",
                }}
              >
                Click to upload
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              style={{ display: "none" }}
            />
          </div>
        </div>

        {/* ✅ Right - Info */}
        <div data-testid="business-info" style={{ flex: "1 1 280px", minWidth: 0 }}>
          {/* Contact Info */}
          <div
            style={{
              border: "1px solid #eee",
              padding: "20px",
              borderRadius: "10px",
              marginBottom: "20px",
              fontSize: "clamp(14px, 4vw, 18px)",
              // NOT white-space: nowrap. It was set here to keep each "Label: value" pair on
              // one line, but white-space inherits, so every value in the card inherited it
              // too: the description drew as one unbroken 703px line inside a 268px card,
              // through its own border and out over the screen, and dragged the content pane
              // sideways with it. Values wrap now, and break-word handles the ones with
              // nothing to break at — a long URL or address is a single "word" to the line
              // breaker. AC-MOBILE-F19.
              overflowWrap: "break-word",
            }}
          >
            <h3 style={{ fontSize: "24px", margin: "10px 0 20px 0" }}>
              Contact Info
            </h3>
            <hr
              style={{
                width: "100%",
                margin: "0 0 20px 0",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
              }}
            />
            {business.email && !isEditMode && (
              <>
                <p>
                  <strong>Email:</strong> {business.email}
                </p>
                <hr
                  style={{
                    width: "100%",
                    border: "none",
                    borderTop: "1px solid rgba(0,0,0,0.1)",
                  }}
                />
              </>
            )}
            <div>
              <strong>Phone:</strong>
              {isEditMode ? (
                <>
                  <input
                    type="text"
                    value={editableFields.phone}
                    onChange={(e) => {
                      setEditableFields({
                        ...editableFields,
                        phone: e.target.value,
                      });
                      // Clear error when user starts typing
                      if (validationErrors.phone) {
                        setValidationErrors({
                          ...validationErrors,
                          phone: "",
                        });
                      }
                    }}
                    onBlur={() => {
                      const error = validatePhone(editableFields.phone);
                      setValidationErrors({
                        ...validationErrors,
                        phone: error,
                      });
                    }}
                    style={{
                      display: "block",

                      width: "100%",
                      marginTop: "8px",
                      padding: "8px",
                      border: validationErrors.phone
                        ? "1px solid #CF0A0A"
                        : "1px solid #ddd",
                      borderRadius: "5px",
                      fontSize: "16px",
                    }}
                  />
                  {validationErrors.phone && (
                    <span
                      style={{
                        color: "#CF0A0A",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {validationErrors.phone}
                    </span>
                  )}
                </>
              ) : (
                business.phone && <span> {business.phone}</span>
              )}
            </div>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                margin: "10px 0",
              }}
            />
            <div>
              <strong>Owner:</strong>
              {isEditMode ? (
                <input
                  type="text"
                  value={editableFields.ownerName}
                  onChange={(e) =>
                    setEditableFields({
                      ...editableFields,
                      ownerName: e.target.value,
                    })
                  }
                  style={{
                    display: "block",

                    width: "100%",
                    marginTop: "8px",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "16px",
                  }}
                />
              ) : (
                business.ownerName && <span> {business.ownerName}</span>
              )}
            </div>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                margin: "10px 0",
              }}
            />
            <div>
              <strong>Website:</strong>
              {isEditMode ? (
                <>
                  <input
                    type="text"
                    value={editableFields.website}
                    onChange={(e) => {
                      setEditableFields({
                        ...editableFields,
                        website: e.target.value,
                      });
                      // Clear error when user starts typing
                      if (validationErrors.website) {
                        setValidationErrors({
                          ...validationErrors,
                          website: "",
                        });
                      }
                    }}
                    onBlur={() => {
                      const error = validateWebsite(editableFields.website);
                      setValidationErrors({
                        ...validationErrors,
                        website: error,
                      });
                    }}
                    style={{
                      display: "block",

                      width: "100%",
                      marginTop: "8px",
                      padding: "8px",
                      border: validationErrors.website
                        ? "1px solid #CF0A0A"
                        : "1px solid #ddd",
                      borderRadius: "5px",
                      fontSize: "16px",
                    }}
                  />
                  {validationErrors.website && (
                    <span
                      style={{
                        color: "#CF0A0A",
                        fontSize: "12px",
                        marginTop: "4px",
                        display: "block",
                      }}
                    >
                      {validationErrors.website}
                    </span>
                  )}
                </>
              ) : (
                business.website && (
                  <span>
                    {" "}
                    <a
                      href={business.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#DE810E" }}
                    >
                      {business.website}
                    </a>
                  </span>
                )
              )}
            </div>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                margin: "10px 0",
              }}
            />
            <div>
              <strong>Description:</strong>
              {isEditMode ? (
                <textarea
                  value={editableFields.description}
                  onChange={(e) =>
                    setEditableFields({
                      ...editableFields,
                      description: e.target.value,
                    })
                  }
                  style={{
                    display: "block",

                    width: "100%",
                    marginTop: "8px",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "16px",
                    minHeight: "100px",
                    resize: "vertical",
                  }}
                />
              ) : (
                business.description && (
                  <span style={{ margin: "10px 0", display: "block" }}>
                    {business.description}
                  </span>
                )
              )}
            </div>
          </div>

          {/* Business Address */}
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: "10px",
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3 style={{ fontSize: "24px", margin: "10px 0 20px 0" }}>
              Business Address
            </h3>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
              }}
            />
            {isEditMode ? (
              <textarea
                value={editableFields.address}
                onChange={(e) =>
                  setEditableFields({
                    ...editableFields,
                    address: e.target.value,
                  })
                }
                style={{
                  display: "block",

                  width: "100%",
                  marginTop: "8px",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                  fontSize: "16px",
                  minHeight: "60px",
                  resize: "vertical",
                }}
              />
            ) : (
              <p style={{ margin: "10" }}>{business.address}</p>
            )}
          </div>

          {/* Payment */}
          <div
            style={{
              border: "1px solid #eee",
              borderRadius: "10px",
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3 style={{ fontSize: "24px", margin: "10px 0 20px 0" }}>
              Payment
            </h3>
            <hr
              style={{
                width: "100%",
                border: "none",
                borderTop: "1px solid rgba(0,0,0,0.1)",
              }}
            />
            {!business.stripeVendorId ? (
              <p style={{ fontSize: "16px", color: "#777", margin: 0 }}>
                No payout account connected.
              </p>
            ) : payoutLoading ? (
              <p style={{ fontSize: "16px", color: "#777", margin: 0 }}>
                Loading payment information...
              </p>
            ) : payoutError ? (
              <p style={{ fontSize: "16px", color: "#CF0A0A", margin: 0 }}>
                {payoutError}
              </p>
            ) : !payoutMethod || !payoutMethod.last4 ? (
              <p style={{ fontSize: "16px", color: "#777", margin: 0 }}>
                No default payout method on file.
              </p>
            ) : payoutMethod.payoutMethodType === "bank_account" ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "rgba(222, 129, 14, 0.1)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#DE810E",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  BANK
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      margin: 0,
                    }}
                  >
                    {payoutMethod.bankName || "Bank account"} ••••{" "}
                    {payoutMethod.last4}
                  </p>
                  {payoutMethod.currency && (
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#777",
                        margin: "4px 0 0 0",
                        textTransform: "uppercase",
                      }}
                    >
                      {payoutMethod.currency}
                      {payoutMethod.country ? ` · ${payoutMethod.country}` : ""}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    background: "rgba(222, 129, 14, 0.1)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    color: "#DE810E",
                    fontWeight: "bold",
                    fontSize: "13px",
                    textTransform: "capitalize",
                  }}
                >
                  {payoutMethod.brand || "Card"}
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "500",
                      margin: 0,
                    }}
                  >
                    **** **** **** {payoutMethod.last4}
                  </p>
                  {(payoutMethod.expMonth || payoutMethod.expYear) && (
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#777",
                        margin: "4px 0 0 0",
                      }}
                    >
                      Exp {String(payoutMethod.expMonth || "").padStart(2, "0")}
                      /{payoutMethod.expYear}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              // Three 167px buttons plus gaps need 521px against ~310 on a phone. With
              // justifyContent: flex-end the surplus ran off the LEFT edge, where nothing can
              // scroll to it — the Save button was simply gone. AC-MOBILE-F10.
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            {isEditMode ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    flex: "1 1 0",

                    minWidth: 0,
                    maxWidth: "167px",
                    height: "59px",
                    padding: "12px clamp(8px, 3vw, 30px)",
                    borderRadius: "30px",
                    border: "none",
                    background: saving ? "#ccc" : "#DE810E",
                    color: "white",
                    fontWeight: "bold",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: "clamp(14px, 4vw, 18px)",

                    whiteSpace: "nowrap",
                  }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  style={{
                    flex: "1 1 0",

                    minWidth: 0,
                    maxWidth: "167px",
                    height: "59px",
                    padding: "12px clamp(8px, 3vw, 30px)",
                    borderRadius: "30px",
                    border: "1px solid #DE810E",
                    background: "white",
                    color: "#DE810E",
                    fontWeight: "bold",
                    cursor: saving ? "not-allowed" : "pointer",
                    fontSize: "clamp(14px, 4vw, 18px)",

                    whiteSpace: "nowrap",
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={handleEdit}
                style={{
                  flex: "1 1 0",

                  minWidth: 0,

                  maxWidth: "167px",
                  height: "59px",
                  padding: "12px clamp(8px, 3vw, 30px)",
                  borderRadius: "30px",
                  border: "none",
                  background: "#DE810E",
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                  fontSize: "clamp(14px, 4vw, 18px)",

                  whiteSpace: "nowrap",
                }}
              >
                Edit
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              disabled={saving || isEditMode}
              style={{
                flex: "1 1 0",

                minWidth: 0,

                maxWidth: "167px",
                height: "59px",
                padding: "12px clamp(8px, 3vw, 30px)",
                borderRadius: "30px",
                border: "1px solid #CF0A0A",
                background: saving || isEditMode ? "#f5f5f5" : "white",
                color: saving || isEditMode ? "#999" : "#CF0A0A",
                fontWeight: "bold",
                cursor: saving || isEditMode ? "not-allowed" : "pointer",
                fontSize: "clamp(14px, 4vw, 18px)",

                whiteSpace: "nowrap",
              }}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BusinessProfileDetail;

